import {
  BACKUP_FREQUENCY_TITLES,
  BACKUP_MAX_PLACES,
  BACKUP_PORTAL_KEY,
  type BackupFrequency,
  type BackupPlaceInput,
  type BackupPlaceView,
  type BackupRunView,
  type BackupSettings,
  type BackupsState,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const KEY = ['admin', 'backups'];
const WEEKDAYS = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
const tall = 'h-11 md:h-10';

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 ** 3).toFixed(2)} ГБ`;
}

const STATUS: Record<string, { tone: 'success' | 'danger' | 'info' | 'neutral'; title: string }> = {
  ok: { tone: 'success', title: 'В порядке' },
  failed: { tone: 'danger', title: 'Сбой' },
  running: { tone: 'info', title: 'Идёт' },
  queued: { tone: 'neutral', title: 'В очереди' },
};

function errText(e: unknown): string {
  return e instanceof Error ? e.message : 'Не получилось';
}

/* ───────────── Расписание и хранение ───────────── */

function SchedulePanel({ state }: { state: BackupsState }) {
  const qc = useQueryClient();
  const [f, setF] = useState<BackupSettings>(state.settings);
  useEffect(() => setF(state.settings), [state.settings]);
  const num = (k: keyof BackupSettings, v: string) => setF({ ...f, [k]: Number(v) || 0 });
  const save = useMutation({
    mutationFn: (b: BackupSettings) =>
      api<BackupSettings>('/admin/backups/settings', { method: 'PUT', body: b }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast('Расписание сохранено');
    },
  });
  return (
    <Panel title="Как часто и сколько хранить" meta="Время — по часовому поясу компании">
      <form
        className="flex max-w-3xl flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(f);
        }}
      >
        <p className="text-[13px] leading-5 text-ink-2">
          В хранилище портала копия делается раз в сутки и хранится не меньше {state.portal.minKeepDaily}{' '}
          суток — это отключить нельзя. Здесь вы задаёте расписание для своих мест хранения и можете хранить
          копии в хранилище портала дольше.
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Как часто" htmlFor="bk-freq">
            <Select
              id="bk-freq"
              className={tall}
              value={f.frequency}
              onChange={(e) => setF({ ...f, frequency: e.target.value as BackupFrequency })}
            >
              {(Object.keys(BACKUP_FREQUENCY_TITLES) as BackupFrequency[]).map((k) => (
                <option key={k} value={k}>
                  {BACKUP_FREQUENCY_TITLES[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Во сколько" htmlFor="bk-time">
            <Input
              id="bk-time"
              type="time"
              className={tall}
              value={f.time}
              onChange={(e) => setF({ ...f, time: e.target.value })}
            />
          </Field>
          {f.frequency === 'weekly' ? (
            <Field label="В какой день недели" htmlFor="bk-wd">
              <Select
                id="bk-wd"
                className={tall}
                value={f.weekday}
                onChange={(e) => num('weekday', e.target.value)}
              >
                {WEEKDAYS.map((w, i) => (
                  <option key={w} value={i + 1}>
                    {w}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {f.frequency === 'monthly' ? (
            <Field label="Какого числа" htmlFor="bk-md" hint="До 28-го, чтобы число было в каждом месяце">
              <Input
                id="bk-md"
                type="number"
                min={1}
                max={28}
                className={tall}
                value={f.monthDay}
                onChange={(e) => num('monthDay', e.target.value)}
              />
            </Field>
          ) : null}
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Свежих копий" htmlFor="bk-kd" hint="Последние по порядку">
            <Input
              id="bk-kd"
              type="number"
              min={1}
              max={400}
              className={tall}
              value={f.keepDaily}
              onChange={(e) => num('keepDaily', e.target.value)}
            />
          </Field>
          <Field label="Недельных копий" htmlFor="bk-kw" hint="По одной за неделю, самая ранняя">
            <Input
              id="bk-kw"
              type="number"
              min={0}
              max={104}
              className={tall}
              value={f.keepWeekly}
              onChange={(e) => num('keepWeekly', e.target.value)}
            />
          </Field>
          <Field label="Месячных копий" htmlFor="bk-km" hint="По одной за месяц, самая ранняя">
            <Input
              id="bk-km"
              type="number"
              min={0}
              max={120}
              className={tall}
              value={f.keepMonthly}
              onChange={(e) => num('keepMonthly', e.target.value)}
            />
          </Field>
        </div>
        <Field
          label="Проверять восстановление, раз в дней"
          htmlFor="bk-ve"
          hint="Не реже раза в месяц (до 31 дня)"
        >
          <Input
            id="bk-ve"
            type="number"
            min={1}
            max={31}
            className={`${tall} sm:max-w-40`}
            value={f.verifyEveryDays}
            onChange={(e) => num('verifyEveryDays', e.target.value)}
          />
        </Field>
        {save.isError ? <ErrorBox error={save.error} /> : null}
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" variant="primary" loading={save.isPending}>
            Сохранить
          </Button>
          {state.nextRunAt ? (
            <span className="text-[13px] text-ink-2">
              Следующая копия по расписанию: {dateTime(state.nextRunAt)}
            </span>
          ) : null}
        </div>
      </form>
    </Panel>
  );
}

/* ───────────── Места хранения ───────────── */

const EMPTY: BackupPlaceInput = {
  name: '',
  endpoint: 'https://',
  bucket: '',
  region: 'auto',
  prefix: '',
  forcePathStyle: true,
  country: 'KZ',
  enabled: true,
  accessKeyId: '',
  secretAccessKey: '',
};

function PlaceForm({ place, onDone }: { place: BackupPlaceView | null; onDone: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<BackupPlaceInput>(
    place
      ? {
          name: place.name,
          endpoint: place.endpoint,
          bucket: place.bucket,
          region: place.region,
          prefix: place.prefix,
          forcePathStyle: place.forcePathStyle,
          country: place.country,
          enabled: place.enabled,
          accessKeyId: '',
          secretAccessKey: '',
        }
      : EMPTY,
  );
  const save = useMutation({
    mutationFn: (b: BackupPlaceInput) =>
      api<BackupPlaceView>(place ? `/admin/backups/places/${place.id}` : '/admin/backups/places', {
        method: place ? 'PUT' : 'POST',
        body: {
          ...b,
          accessKeyId: b.accessKeyId || undefined,
          secretAccessKey: b.secretAccessKey || undefined,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast(place ? 'Место сохранено' : 'Место добавлено');
      onDone();
    },
  });
  const set = <K extends keyof BackupPlaceInput>(k: K, v: BackupPlaceInput[K]) => setF({ ...f, [k]: v });
  return (
    <form
      className="flex flex-col gap-4 rounded-md border border-line bg-surface-2 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(f);
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Название места" htmlFor="pl-name">
          <Input
            id="pl-name"
            className={tall}
            value={f.name}
            maxLength={80}
            onChange={(e) => set('name', e.target.value)}
            required
          />
        </Field>
        <Field
          label="Где находится хранилище"
          htmlFor="pl-country"
          hint="Персональные данные граждан РК должны храниться в РК"
        >
          <Select
            id="pl-country"
            className={tall}
            value={f.country}
            onChange={(e) => set('country', e.target.value as 'KZ' | 'other')}
          >
            <option value="KZ">В Казахстане</option>
            <option value="other">В другой стране</option>
          </Select>
        </Field>
        <Field label="Адрес хранилища (S3)" htmlFor="pl-ep" hint="Например, https://s3.example.kz">
          <Input
            id="pl-ep"
            className={tall}
            value={f.endpoint}
            onChange={(e) => set('endpoint', e.target.value)}
            required
          />
        </Field>
        <Field label="Бакет" htmlFor="pl-bucket">
          <Input
            id="pl-bucket"
            className={tall}
            value={f.bucket}
            onChange={(e) => set('bucket', e.target.value)}
            required
          />
        </Field>
        <Field label="Регион" htmlFor="pl-region">
          <Input
            id="pl-region"
            className={tall}
            value={f.region}
            onChange={(e) => set('region', e.target.value)}
          />
        </Field>
        <Field label="Каталог внутри бакета" htmlFor="pl-prefix" hint="Можно оставить пустым">
          <Input
            id="pl-prefix"
            className={tall}
            value={f.prefix}
            onChange={(e) => set('prefix', e.target.value)}
          />
        </Field>
        <Field
          label="Ключ доступа"
          htmlFor="pl-ak"
          hint={place?.hasSecret ? 'Задан. Оставьте пустым, чтобы не менять' : undefined}
        >
          <Input
            id="pl-ak"
            className={tall}
            value={f.accessKeyId ?? ''}
            autoComplete="off"
            onChange={(e) => set('accessKeyId', e.target.value)}
            required={!place}
          />
        </Field>
        <Field
          label="Секретный ключ"
          htmlFor="pl-sk"
          hint="Хранится зашифрованным и больше нигде не показывается"
        >
          <Input
            id="pl-sk"
            type="password"
            className={tall}
            value={f.secretAccessKey ?? ''}
            autoComplete="new-password"
            onChange={(e) => set('secretAccessKey', e.target.value)}
            required={!place}
          />
        </Field>
      </div>
      <label className="flex min-h-11 items-center gap-2.5 text-[14px] text-ink">
        <input
          type="checkbox"
          className="size-4 accent-[var(--color-ink)]"
          checked={f.forcePathStyle}
          onChange={(e) => set('forcePathStyle', e.target.checked)}
        />
        Адрес вида «хранилище/бакет» (нужен для MinIO и большинства своих хранилищ)
      </label>
      <label className="flex min-h-11 items-center gap-2.5 text-[14px] text-ink">
        <input
          type="checkbox"
          className="size-4 accent-[var(--color-ink)]"
          checked={f.enabled}
          onChange={(e) => set('enabled', e.target.checked)}
        />
        Делать копии в это место
      </label>
      {save.isError ? <ErrorBox error={save.error} /> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" loading={save.isPending}>
          {place ? 'Сохранить' : 'Добавить'}
        </Button>
        <Button onClick={onDone}>Отмена</Button>
      </div>
    </form>
  );
}

function PlacesPanel({ state }: { state: BackupsState }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<BackupPlaceView | 'new' | null>(null);
  const check = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean; message: string }>(`/admin/backups/places/${id}/check`, { method: 'POST' }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast(r.message, r.ok ? 'success' : 'danger');
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/admin/backups/places/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast('Место убрано. Копии в самом хранилище остались');
    },
  });
  return (
    <Panel
      title="Куда делать копии"
      meta={`Своих мест — не больше ${BACKUP_MAX_PLACES}`}
      actions={
        edit || state.isDemo || state.places.length >= BACKUP_MAX_PLACES ? null : (
          <Button size="sm" onClick={() => setEdit('new')}>
            Добавить место
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-md border border-line p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-medium text-ink">Хранилище портала</div>
            <Badge tone={state.portal.configured ? 'success' : 'warning'}>
              {state.portal.configured ? 'Включено' : 'Не настроено'}
            </Badge>
          </div>
          <p className="mt-1 text-[13px] leading-5 text-ink-2">
            {state.portal.configured
              ? `Занято ${size(state.portal.usedBytes)}${state.portal.limitBytes ? ` из ${size(state.portal.limitBytes)} по тарифу` : ''}.`
              : 'Хранилище для копий на портале ещё не подключено. Пока этого нет, копии идут только в ваши места.'}
          </p>
          {state.portal.warning ? (
            <p className="mt-2 text-[13px] leading-5 text-warning">{state.portal.warning}</p>
          ) : null}
        </div>
        {state.isDemo ? (
          <p className="text-[13px] text-ink-2">В демо-компании внешние места хранения выключены.</p>
        ) : null}
        {state.places.map((p) =>
          edit && typeof edit !== 'string' && edit.id === p.id ? (
            <PlaceForm key={p.id} place={p} onDone={() => setEdit(null)} />
          ) : (
            <div key={p.id} className="rounded-md border border-line p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 font-medium [overflow-wrap:anywhere] text-ink">{p.name}</div>
                <div className="flex flex-wrap gap-1.5">
                  {!p.enabled ? <Badge>Выключено</Badge> : null}
                  {p.lastCheckOk === true ? <Badge tone="success">Связь есть</Badge> : null}
                  {p.lastCheckOk === false ? <Badge tone="danger">Нет связи</Badge> : null}
                </div>
              </div>
              <p className="mt-1 text-[13px] leading-5 [overflow-wrap:anywhere] text-ink-2">
                {p.endpoint} · бакет {p.bucket}
                {p.prefix ? ` · ${p.prefix}` : ''} · {p.country === 'KZ' ? 'Казахстан' : 'другая страна'}
              </p>
              {p.lastCheckAt ? (
                <p className="mt-1 text-[12px] leading-4 [overflow-wrap:anywhere] text-ink-2">
                  Проверка {dateTime(p.lastCheckAt)}: {p.lastCheckMessage}
                </p>
              ) : null}
              {p.warning ? <p className="mt-2 text-[13px] leading-5 text-warning">{p.warning}</p> : null}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  loading={check.isPending && check.variables === p.id}
                  onClick={() => check.mutate(p.id)}
                >
                  Проверить связь
                </Button>
                <Button size="sm" onClick={() => setEdit(p)}>
                  Изменить
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    if (confirm(`Убрать место «${p.name}»? Уже сделанные копии в хранилище останутся.`))
                      del.mutate(p.id);
                  }}
                >
                  Убрать
                </Button>
              </div>
            </div>
          ),
        )}
        {edit === 'new' ? <PlaceForm place={null} onDone={() => setEdit(null)} /> : null}
        {check.isError ? <ErrorBox error={check.error} /> : null}
        {del.isError ? <ErrorBox error={del.error} /> : null}
      </div>
    </Panel>
  );
}

/* ───────────── Копии ───────────── */

function PasswordAsk({
  label,
  onSubmit,
  pending,
  error,
}: {
  label: string;
  onSubmit: (p: string) => void;
  pending: boolean;
  error: unknown;
}) {
  const [pw, setPw] = useState('');
  const id = useId();
  return (
    <form
      className="mt-2 flex flex-col gap-2 rounded-md bg-surface-2 p-3 sm:flex-row sm:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(pw);
      }}
    >
      <Field label="Ваш пароль для подтверждения" htmlFor={id} className="sm:flex-1">
        <Input
          id={id}
          type="password"
          className={tall}
          value={pw}
          autoComplete="current-password"
          onChange={(e) => setPw(e.target.value)}
          required
        />
      </Field>
      <Button type="submit" variant="primary" loading={pending}>
        {label}
      </Button>
      {error ? <span className="text-[12px] text-danger">{errText(error)}</span> : null}
    </form>
  );
}

function RunRow({ run, owner }: { run: BackupRunView; owner: boolean }) {
  const [ask, setAsk] = useState(false);
  const dl = useMutation({
    mutationFn: (password: string) =>
      api<{ url: string }>(`/admin/backups/runs/${run.id}/download`, { method: 'POST', body: { password } }),
    onSuccess: (r) => {
      setAsk(false);
      window.location.href = r.url;
    },
  });
  const st = STATUS[run.status] ?? STATUS.queued!;
  return (
    <li className="rounded-md border border-line p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[14px] font-medium text-ink">{dateTime(run.startedAt)}</div>
        <div className="flex flex-wrap gap-1.5">
          <Badge tone={st.tone}>{st.title}</Badge>
          {run.trigger === 'manual' ? <Badge>По кнопке</Badge> : null}
          {run.verifyStatus ? (
            <Badge tone={run.verifyStatus === 'ok' ? 'success' : 'danger'}>
              Проверка восстановления: {run.verifyStatus === 'ok' ? 'в порядке' : 'сбой'}
            </Badge>
          ) : null}
        </div>
      </div>
      <p className="mt-1 text-[13px] leading-5 text-ink-2">
        {run.placeName}
        {run.status === 'ok'
          ? ` · ${size(run.sizeBytes)} · записей ${run.rowsCount}, файлов ${run.filesTotal} (новых ${run.filesNew})`
          : ''}
      </p>
      {run.error ? (
        <p className="mt-1 text-[12px] leading-4 [overflow-wrap:anywhere] text-danger">{run.error}</p>
      ) : null}
      {run.status === 'ok' && owner ? (
        <div className="mt-2">
          {ask ? (
            <PasswordAsk
              label="Скачать копию"
              pending={dl.isPending}
              error={dl.error}
              onSubmit={(p) => dl.mutate(p)}
            />
          ) : (
            <Button size="sm" onClick={() => setAsk(true)}>
              Скачать
            </Button>
          )}
        </div>
      ) : null}
    </li>
  );
}

function RunsPanel({ state }: { state: BackupsState }) {
  const qc = useQueryClient();
  const [place, setPlace] = useState(BACKUP_PORTAL_KEY);
  const now = useMutation({
    mutationFn: () => api('/admin/backups/run', { method: 'POST', body: { place } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast('Копия поставлена в очередь: она начнётся в течение нескольких минут');
    },
  });
  const options = [
    ...(state.portal.configured ? [{ id: BACKUP_PORTAL_KEY, name: 'Хранилище портала' }] : []),
    ...state.places.filter((p) => p.enabled).map((p) => ({ id: p.id, name: p.name })),
  ];
  const [reveal, setReveal] = useState(false);
  const [key, setKey] = useState<{ key: string; version: number } | null>(null);
  const getKey = useMutation({
    mutationFn: (password: string) =>
      api<{ key: string; version: number }>('/admin/backups/key', { method: 'POST', body: { password } }),
    onSuccess: (r) => {
      setKey(r);
      setReveal(false);
    },
  });
  return (
    <>
      <Panel
        title="Копии"
        meta={state.runs.length ? `Последние ${state.runs.length}` : undefined}
        actions={
          state.isDemo || !options.length ? null : (
            <>
              <Select
                aria-label="Куда сделать копию"
                className={`${tall} md:w-56`}
                value={place}
                onChange={(e) => setPlace(e.target.value)}
              >
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
              <Button size="sm" variant="primary" loading={now.isPending} onClick={() => now.mutate()}>
                Сделать копию сейчас
              </Button>
            </>
          )
        }
      >
        {!state.serverKeyReady ? (
          <p className="mb-3 text-[13px] leading-5 text-danger">
            На сервере портала не задан ключ шифрования секретов. Пока его нет, копии не делаются: обратитесь
            к владельцу портала.
          </p>
        ) : null}
        {state.isDemo ? (
          <p className="mb-3 text-[13px] leading-5 text-ink-2">
            Это демо-компания: ниже показан пример списка. Настоящие копии демо не делает.
          </p>
        ) : null}
        {now.isError ? <ErrorBox error={now.error} className="mb-3" /> : null}
        {state.runs.length ? (
          <ul className="flex flex-col gap-2">
            {state.runs.map((r) => (
              <RunRow key={r.id} run={r} owner={state.isOwner && !state.isDemo} />
            ))}
          </ul>
        ) : (
          <p className="text-[14px] text-ink-2">
            Копий пока нет. Первая сделается по расписанию или по кнопке.
          </p>
        )}
        {!state.isOwner ? (
          <p className="mt-3 text-[12px] leading-4 text-ink-2">
            Скачивать копии и получать ключ может только владелец компании.
          </p>
        ) : null}
      </Panel>
      {state.isOwner && !state.isDemo ? (
        <Panel
          title="Ключ копий"
          meta={state.keyVersion ? `Версия ${state.keyVersion}` : 'Появится с первой копией'}
        >
          <div className="max-w-2xl text-[14px] leading-6 text-ink-2">
            <p>
              Копии зашифрованы ключом вашей компании. Без него копию, которая лежит в вашем собственном
              хранилище вне портала, не открыть. Сохраните ключ в надёжном месте отдельно от копий: тому, у
              кого есть и ключ, и копия, доступны все данные компании.
            </p>
            {key ? (
              <div className="mt-3 flex flex-col gap-2">
                <code className="rounded-md bg-surface-2 p-3 text-[13px] break-all text-ink">{key.key}</code>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      void navigator.clipboard?.writeText(key.key).then(() => toast('Ключ скопирован'));
                    }}
                  >
                    Скопировать
                  </Button>
                  <Button size="sm" onClick={() => setKey(null)}>
                    Скрыть
                  </Button>
                </div>
              </div>
            ) : reveal ? (
              <PasswordAsk
                label="Показать ключ"
                pending={getKey.isPending}
                error={getKey.error}
                onSubmit={(p) => getKey.mutate(p)}
              />
            ) : (
              <Button className="mt-3" size="sm" disabled={!state.keyVersion} onClick={() => setReveal(true)}>
                Получить ключ
              </Button>
            )}
          </div>
        </Panel>
      ) : null}
    </>
  );
}

export function BackupsPage() {
  const q = useQuery({
    queryKey: KEY,
    queryFn: () => api<BackupsState>('/admin/backups'),
    refetchInterval: 30_000,
  });
  if (q.isError) return <ErrorBox error={q.error} />;
  if (q.isPending) return <Spinner />;
  const state = q.data;
  return (
    <div className="flex flex-col gap-5">
      <SchedulePanel state={state} />
      <PlacesPanel state={state} />
      <RunsPanel state={state} />
    </div>
  );
}
